/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { SESSION_STORAGE_USER_PARAMETERS_KEY } from "constants/sessionStorageKeys"
import { TAB_IDS } from "constants/tabIds"

import { RootState } from "app/store"
import CalculateButton from "components/buttons/CalculateButton"
import DisclaimerFooter from "components/DisclaimerFooter"
import SentryTestButton from "components/SentryTestButton"
import TabContainer from "components/TabContainer"
import Results from "features/results"
import AllCashFlows from "features/userParameters/AllCashFlows"
import Assets, { AssetsType } from "features/userParameters/Assets"
import GeneralParameters from "features/userParameters/GeneralParameters"
import {
  UserParametersForm,
  UserParametersFormProps,
} from "features/userParameters/UserParametersForm"
import {
  CurrentUserParameters,
  initialUserParameters,
} from "features/userParameters/UserParametersSchemata"
import XlsxDownload from "features/userParameters/XlsxDownload"
import XlsxUpload from "features/userParameters/XlsxUpload"
import { useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router"
import { Sentry } from "services/sentry"
import { pageMetaData, updateMetaTags } from "utils/metaTags"
import { DeepReadonly } from "utils/readonly"
import { safeSessionStorageGetItem, safeSessionStorageRemoveItem } from "utils/safeSessionStorage"

const Calculator = () => {
  // Check if results exist to conditionally show the Results tab
  const results = useSelector((state: DeepReadonly<RootState>) => state.results)

  /* code enabling parameters to be duplicated with browser duplication begins (see also UserParametersForm) */

  // Check if this is a hard refresh (reload) - if so, ignore sessionStorage
  const isHardRefresh = useMemo(() => {
    if (typeof window !== "undefined") {
      // Check if the page was loaded via reload
      const navigationEntries = window.performance.getEntriesByType("navigation")
      if (navigationEntries.length > 0) {
        const navigationType = navigationEntries[0] as PerformanceNavigationTiming
        return navigationType.type === "reload"
      }
    }
    return false
  }, [])

  const storedParams = !isHardRefresh
    ? safeSessionStorageGetItem(SESSION_STORAGE_USER_PARAMETERS_KEY)
    : null
  const defaultValues = useMemo(() => {
    if (!storedParams) return initialUserParameters

    try {
      return JSON.parse(storedParams) as CurrentUserParameters
    } catch (error) {
      // AIDEV-NOTE: "storage_parsing_error" is intentionally distinct from
      // safeSessionStorage's "storage_access_error" tag - this fires when sessionStorage was
      // readable but returned malformed JSON (data corruption), not when the sessionStorage API
      // itself threw (access denial, e.g. SecurityError). Keep the names distinguishable so
      // Sentry dashboards can separate the two failure classes.
      Sentry.captureException(error, {
        tags: { errorType: "storage_parsing_error", component: "Calculator" },
      })

      // Clear corrupted storage and fall back to defaults
      safeSessionStorageRemoveItem(SESSION_STORAGE_USER_PARAMETERS_KEY)
      return initialUserParameters
    }
  }, [storedParams])

  useEffect(() => {
    // This runs only once on page load to clear sessionStorage after we've captured the stored params,
    // since we only use this data to initialize the form upon duplication.
    if (storedParams) {
      safeSessionStorageRemoveItem(SESSION_STORAGE_USER_PARAMETERS_KEY)
    }
    // Also clear sessionStorage on hard refresh to ensure clean state
    if (isHardRefresh) {
      safeSessionStorageRemoveItem(SESSION_STORAGE_USER_PARAMETERS_KEY)
    }
  }, [storedParams, isHardRefresh])

  /* code enabling parameters to be duplicated with browser duplication ends (see also UserParametersForm) */

  // Update meta tags for home page
  useEffect(() => {
    const { title, description, keywords } = pageMetaData.home
    updateMetaTags(title, description, keywords)
  }, [])

  // AIDEV-NOTE: Keep the UserParametersForm render-prop boundary readonly-safe while preserving tab wiring and behavior.
  const renderCalculatorTabs = (...renderArgsInput: readonly unknown[]) => {
    const [
      formMethods,
      activeTab,
      setActiveTab,
      tabErrorMap,
      loading,
      handleCalculateClick,
      triggerCalculation,
      resetFromUpload,
    ] = renderArgsInput as Parameters<UserParametersFormProps["children"]>

    // Get allocation mode for conditionally showing tabs
    const allocationMode = formMethods.watch("allocationMode")

    return (
      <>
        <TabContainer
          activeTab={activeTab}
          onTabChange={tabId => {
            // Prevent navigation to Results tab if no results exist
            if (tabId === TAB_IDS.RESULTS && !results.results) {
              return
            }
            setActiveTab(tabId)
          }}
          tabs={[
            {
              id: TAB_IDS.INSTRUCTIONS,
              title: "Instructions",
              errorCount: 0,
              children: (
                <>
                  <SentryTestButton />
                  <p>
                    You&apos;re ready to retire &mdash; but how much can you withdraw from your
                    portfolio every year without running out of money?
                  </p>
                  <p>
                    Safe Retirement Spending allows you to simulate how your retirement plan would
                    have performed in the past, across spans dating back to 1871. This lets you see
                    how it would have performed under various starting conditions, such as stock
                    market peaks and high CAPE ratios.
                  </p>
                  <div className="card mt-4 mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Quick Start</h5>
                    </div>
                    <div className="card-body">
                      <p className="card-body mb-0">
                        For more detailed instructions, see our <Link to="/guide/">Guide</Link>.
                      </p>
                      <ol>
                        <li className="mb-2">
                          <strong>General Parameters</strong> - Enter retirement details such as
                          planned initial portfolio value, retirement start date, horizon, and final
                          value targeted.
                        </li>
                        <li className="mb-2">
                          <strong>Assets</strong> - Specify your asset allocation across stocks,
                          bonds, cash, and alternatives.
                          <ul className="mt-1">
                            <li>
                              If you choose <strong>Glide path</strong> allocation mode, you&apos;ll
                              set both Initial Assets and Final Assets and your assets will shift at
                              a fixed rate between these.
                            </li>
                            <li>
                              Include your expense ratio (investment fees) so fees are reflected in
                              the simulation.
                            </li>
                            <li>
                              See the Guide page for instructions on the Fama-French factors. If in
                              doubt, leave them at 0%.
                            </li>
                          </ul>
                        </li>
                        <li className="mb-2">
                          <strong>Cash Flows</strong> - Add expected income and expenses during
                          retirement.
                          <ul className="mt-1">
                            <li>
                              <strong>Positive numbers</strong> for income (pensions, Social
                              Security, rental income)
                            </li>
                            <li>
                              <strong>Negative numbers</strong> for expenses (mortgage, healthcare
                              premiums)
                            </li>
                            <li>
                              <strong>Inflation-adjusted examples:</strong>
                              <ul>
                                <li>+$2,500 monthly: Social Security (starts at age 67)</li>
                                <li>+$1,800 monthly: Public pension (starts immediately)</li>
                                <li>-$15,000 annually: Healthcare premiums</li>
                              </ul>
                            </li>
                            <li>
                              <strong>Fixed-amount examples:</strong>
                              <ul>
                                <li>-$1,500 monthly: Fixed mortgage payment (ends in 2035)</li>
                              </ul>
                            </li>
                          </ul>
                        </li>
                        <li className="mb-2">
                          <strong>Save/Load Plan</strong> - Save your retirement parameters as an
                          .xlsx file or load a previously saved plan.
                        </li>
                        <li className="mb-2">
                          <strong>Results</strong> - Select the retirements you wish to simulate
                          based on ranges of: starting years, initial CAPE ratios, and initial stock
                          market drawdowns.
                          <ul className="mt-1">
                            <li>
                              <strong>Final Portfolio Values</strong>: Visualize the full range of
                              historical outcomes for your retirement plan.
                            </li>
                            <li>
                              <strong>Failure Rates</strong>: See the percentage of times your plan
                              would have fallen short of your target in the past.
                            </li>
                            <li>
                              <strong>Consumption Amounts</strong>: Determine how much you could
                              have safely consumed initially to meet different success targets.
                            </li>
                            <li>
                              <strong>Consumption Rates</strong>: Identify historically safe
                              consumption rates.
                            </li>
                            <li>
                              <strong>About Results</strong>: Dive deeper into explanations of CAPE
                              ratios, drawdowns, and our analysis methodology.
                            </li>
                          </ul>
                        </li>
                      </ol>
                    </div>
                  </div>
                  <div className="card mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Quick Tips</h5>
                    </div>
                    <div className="card-body">
                      <ul className="mb-0">
                        <li className="mb-2">
                          <strong>Save your plan</strong>: Build it over several sessions, adjust it
                          over time, and compare it with other saved plans.
                        </li>
                        <li className="mb-2">
                          <strong>Consider CAPE-based filtering in the Results section</strong>:
                          Historically, a high CAPE ratio (say &gt; 20) at the beginning of a
                          retirement was a risk factor.
                        </li>
                        <li className="mb-2">
                          <strong>Include important cash flows</strong>: Major income and expense
                          streams like pensions, Social Security, and mortgages.
                        </li>
                      </ul>
                    </div>
                  </div>
                  <p>
                    <strong>Ready to start?</strong> Proceed to the{" "}
                    <strong>General Parameters</strong> tab and begin building your personalized
                    retirement analysis.
                  </p>
                </>
              ),
            },
            {
              id: TAB_IDS.GENERAL_PARAMETERS,
              title: "General Parameters",
              errorCount: tabErrorMap[TAB_IDS.GENERAL_PARAMETERS],
              children: (
                <div className="parameters-tab-content">
                  <GeneralParameters />
                </div>
              ),
            },
            {
              id: TAB_IDS.INITIAL_ASSETS,
              title: allocationMode === "static" ? "Assets" : "Initial Assets",
              errorCount: tabErrorMap[TAB_IDS.INITIAL_ASSETS],
              children: (
                <div className="assets-tab-content">
                  <Assets
                    legend={allocationMode === "static" ? "Assets" : "Initial Assets"}
                    assetsType={AssetsType.Initial}
                  />
                </div>
              ),
            },
            ...(allocationMode === "glide path"
              ? [
                  {
                    id: TAB_IDS.FINAL_ASSETS,
                    title: "Final Assets",
                    errorCount: tabErrorMap[TAB_IDS.FINAL_ASSETS],
                    children: (
                      <div className="assets-tab-content">
                        <Assets legend="Final Assets" assetsType={AssetsType.Final} />
                      </div>
                    ),
                  },
                ]
              : []),
            {
              id: TAB_IDS.CASH_FLOWS,
              title: "Cash Flows",
              errorCount: tabErrorMap[TAB_IDS.CASH_FLOWS],
              children: (
                <div className="cash-flows-tab-container">
                  <AllCashFlows />
                </div>
              ),
            },
            {
              id: TAB_IDS.SAVE_LOAD,
              title: "Save/Load Plan",
              errorCount: tabErrorMap[TAB_IDS.SAVE_LOAD],
              children: (
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <XlsxDownload />
                  <XlsxUpload
                    onCalculate={() => {
                      triggerCalculation()
                    }}
                    onUploadReset={resetFromUpload}
                  />
                </div>
              ),
            },
            // Only include the Results tab if results exist
            ...(results.results
              ? [
                  {
                    id: TAB_IDS.RESULTS,
                    title: "Results",
                    children: <Results />,
                    errorCount: 0,
                  },
                ]
              : []),
          ]}
        />

        {activeTab !== TAB_IDS.INSTRUCTIONS && (
          <div className="d-flex w-100">
            <CalculateButton
              id="calculate-results-button"
              loading={loading}
              onClick={handleCalculateClick}
            />
          </div>
        )}

        <DisclaimerFooter />
      </>
    )
  }

  return (
    <UserParametersForm defaultValues={defaultValues}>{renderCalculatorTabs}</UserParametersForm>
  )
}

export default Calculator
